import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Check, Download, FileText, Files, FileType } from 'lucide-react';
import { assetToast } from '@xdocs/i18n';
import type { RenderFormat } from '@xdocs/schemas';
import { Button, Modal, cn, useI18n, useToast } from '@xdocs/ui';
import { JobProgressCard, useJobSnapshot } from '../jobs/index.js';
import { useEditorT } from './useEditorT.js';
import { useExport } from './useExport.js';
import { useFileDownloadUrl } from './useFileDownloadUrl.js';
import styles from './ExportMenu.module.css';

export interface ExportMenuProps {
  documentId: string;
  /** Required — names the asset in the ready toast (§16.1). */
  documentTitle: string;
  versionId: string;
  className?: string | undefined;
}

const FORMAT_ICON: Record<RenderFormat, ReactElement> = {
  pdf: <FileType size={18} aria-hidden="true" />,
  docx: <Files size={18} aria-hidden="true" />,
  md: <FileText size={18} aria-hidden="true" />,
  txt: <FileText size={18} aria-hidden="true" />,
};

const FORMATS: readonly RenderFormat[] = ['pdf', 'docx', 'md', 'txt'];

/**
 * F67 §28.4 export menu: PDF/DOCX/MD/TXT → `export.start` (fast `jobId`
 * reply) → live job progress (reuses `features/jobs`' `JobProgressCard`,
 * the SAME 0.2 job-lifecycle surface as generation) → once the job's
 * `render` checkpoint carries an `artifactFileId`, resolve the presigned
 * download link (`useFileDownloadUrl`) and toast the asset+format by name.
 * Large artifacts never ride the WS payload (§5.7/§5.8) — only the id does.
 */
export function ExportMenu({ documentId, documentTitle, versionId, className }: ExportMenuProps): ReactElement {
  const { t } = useEditorT();
  const { locale } = useI18n();
  const { push: pushToast } = useToast();
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState<RenderFormat | null>(null);
  // A guard, never rendered — a plain ref (not state) so the toast-on-ready
  // effect never itself triggers a React re-render (react-hooks purity:
  // avoid calling setState synchronously from inside an effect body).
  const toastedForJobRef = useRef<string | null>(null);
  const { jobId, isStarting, startError, start, reset } = useExport(documentId, versionId);
  const { snapshot } = useJobSnapshot(jobId);

  const artifactFileId = snapshot?.checkpoints.find((cp) => cp.artifactFileId !== null)?.artifactFileId ?? null;
  const jobSucceeded = snapshot?.job.status === 'succeeded';
  const jobFailed = snapshot?.job.status === 'failed';
  const attemptsExhausted = jobFailed && (snapshot?.job.attempts ?? 0) >= (snapshot?.job.maxAttempts ?? 1);
  const { downloadUrl, isLoading: isResolvingUrl } = useFileDownloadUrl(jobSucceeded ? artifactFileId : null);

  useEffect(() => {
    if (downloadUrl && jobId && toastedForJobRef.current !== jobId && format) {
      pushToast(assetToast(locale, 'success', `${t('editor:toast.exportReady.action')} (${t(`editor:export.format.${format}`)})`, documentTitle));
      toastedForJobRef.current = jobId;
    }
  }, [downloadUrl, jobId, format, documentTitle, locale, pushToast, t]);

  function handleSelectFormat(next: RenderFormat): void {
    setFormat(next);
    toastedForJobRef.current = null;
    start(next);
  }

  function handleStartOver(): void {
    setFormat(null);
    reset();
  }

  function handleClose(): void {
    setOpen(false);
  }

  return (
    <>
      <Button variant="primary" leadingIcon={<Download size={16} aria-hidden="true" />} onClick={() => setOpen(true)} className={className}>
        {t('editor:export.start')}
      </Button>

      <Modal open={open} onClose={handleClose} title={t('editor:export.title')} description={t('editor:export.description', { title: documentTitle })}>
        {!format && (
          <div className={styles['formatList']}>
            {FORMATS.map((f) => (
              <button key={f} type="button" className={styles['formatButton']} onClick={() => handleSelectFormat(f)}>
                {FORMAT_ICON[f]}
                <span>{t(`editor:export.format.${f}`)}</span>
              </button>
            ))}
            <p className={styles['styleWarning']}>{t('editor:export.styleWarning')}</p>
            {startError && (
              <p role="alert" className={styles['styleWarning']}>
                {startError}
              </p>
            )}
          </div>
        )}

        {format && isStarting && (
          <div aria-busy="true">{t('editor:export.preparing')}</div>
        )}

        {format && jobId && !jobSucceeded && (
          <div>
            <JobProgressCard jobId={jobId} />
            {!jobFailed && <p className={styles['styleWarning']}>{t('editor:export.checkpointed')}</p>}
            {jobFailed && attemptsExhausted && (
              <div className={styles['errorState']}>
                <p role="alert" className={styles['styleWarning']}>
                  {t('editor:export.error')}
                </p>
                <Button variant="secondary" onClick={handleStartOver}>
                  {t('editor:export.retry')}
                </Button>
              </div>
            )}
          </div>
        )}

        {format && jobSucceeded && !downloadUrl && (
          <div aria-busy="true">{isResolvingUrl ? t('editor:export.preparing') : null}</div>
        )}

        {format && jobSucceeded && downloadUrl && (
          <div className={cn(styles['readyState'])}>
            <span className={styles['checkIcon']} aria-hidden="true">
              <Check size={20} />
            </span>
            <p className={styles['readyTitle']}>{t('editor:export.ready')}</p>
            <Button variant="primary" onClick={() => window.open(downloadUrl, '_blank', 'noopener,noreferrer')}>
              {t('editor:export.download')}
            </Button>
          </div>
        )}
      </Modal>
    </>
  );
}
